<template>
  <div class="ls-service-details-card" :class="{ 'ls-service-details-card--nonLtrs': !isLtrs }">
    <span
      v-if="!isLtrs"
      class="ls-service-details-card--sub-heading"
      test-id="ls_service_details_card-label"
      >{{ t(serviceType.label) }}</span
    >
    <span v-if="isLtrs" class="ls-service-details-card--header"> {{ t('upcomingService') }} </span>
    <div class="ls-service-details-card--service" test-id="ls_service_details_card-service">
      <LsIcon v-bind="eventDetails.icon" size="lg" test-id="ls_service_details_card-icon" />
      <div v-if="!isLtrs || isServiceWindowConfirmed">
        <span test-id="ls_service_details_card-status">{{ eventDetails.statusText }}</span> <br />
        <span test-id="ls_service_details_card-date">{{
          eventDetails.pending || getDateText(eventDetails)
        }}</span>
      </div>
      <div
        v-if="isLtrs && !isServiceWindowConfirmed"
        class="ls-service-details-card--ltrs-status"
        test-id="ls-service-details-card--ltrs-status"
      >
        <span>{{ t('lawnTreatment.pendingConfirmation') }}</span>
      </div>
      <RouterLink
        v-if="shouldDisplayLongGrassLink"
        :to="{
          name: longGrassParams.screen,
          params: longGrassParams.params,
        }"
        class="ls-service-details-card--long-grass-report"
        test-id="ls_service_details_card-long_grass_report"
      >
        <span>{{ t('longGrass.longGrassReport.title') }}</span>
        <LsIcon class="chevron" color="social-twitter" type="chevron" size="sm" />
      </RouterLink>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import LsIcon from '@/components/common/LsIcon/LsIcon.vue';
import { lsDateHelpers } from 'ls-vue-common';
import VueTypes from 'vue-types';
import { lsScheduleEventServiceItemHelpers } from '@/components/ServiceItems/LsScheduleEventServiceItem/LsScheduleEventServiceItemHelpers';
import { LONG_GRASS_TYPE, SERVICE_TYPES } from '@/constants';
import { lsScheduleServiceItemHelpers } from '@/components/ServiceItems/LsScheduleServiceItem/LsScheduleServiceItemHelpers';
import { getLtrsAttributes } from '@/helpers/ltrs';

export default {
  name: 'LsServiceDetailsCard',
  components: { LsIcon },
  props: {
    serviceType: VueTypes.shape({
      type: VueTypes.string.isRequired,
      label: VueTypes.string.isRequired,
    }).isRequired.loose,
    isLtrs: VueTypes.bool.def(false),
    isServiceWindowConfirmed: VueTypes.bool.def(false),
    scheduleEvent: VueTypes.object.isRequired,
    ltrsGroupedBySchedule: VueTypes.shape({
      id: VueTypes.arrayOf(
        VueTypes.shape({
          windowStart: VueTypes.string,
          windowEnd: VueTypes.string,
          status: VueTypes.string,
        }).loose
      ).def([]),
    }).loose,
    longGrassParams: VueTypes.shape({
      type: VueTypes.string,
      isPending: VueTypes.bool,
      screen: VueTypes.string,
      isScopeAvailable: VueTypes.bool,
      params: VueTypes.shape({
        scheduleevent_id: VueTypes.number,
        report_id: VueTypes.number,
      }).loose,
    }).isRequired.loose,
  },
  computed: {
    eventDetails() {
      const { isPending, type: lgType } = this.longGrassParams;
      const { type: typeOfService } = this.serviceType;

      const isUpcomingService = typeOfService === SERVICE_TYPES.UPCOMING_SERVICE.type;
      const isLGQ = lgType === LONG_GRASS_TYPE.LGQ;

      if (!_.isEmpty(this.longGrassParams) && isUpcomingService && isLGQ && isPending)
        return {
          icon: { type: 'calendar', color: 'primary' },
          statusText: this.t('upcomingService'),
          pending: this.t('pendingLongGrass'),
        };

      if (this.isLtrs) {
        const {
          schedule: { id: scheduleId },
        } = this.scheduleEvent;

        const schedule = {
          ...this.scheduleEvent,
          ...getLtrsAttributes(this.ltrsGroupedBySchedule, { schedule_id: scheduleId }),
        };

        return lsScheduleServiceItemHelpers.getScheduleServiceItemDisplayValues({
          schedule,
        });
      }

      return lsScheduleEventServiceItemHelpers.getScheduleEventServiceItemDisplayValues({
        scheduleEvent: this.scheduleEvent,
      });
    },
    shouldDisplayLongGrassLink() {
      const { longGrassParams } = this;
      if (_.isEmpty(longGrassParams)) return false;

      const { isPending, type: lgType, isScopeAvailable } = longGrassParams;
      const { type: typeOfService } = this.serviceType;

      const isLGF = lgType === LONG_GRASS_TYPE.LGF;
      const isLGQ = lgType === LONG_GRASS_TYPE.LGQ;
      const isUpcomingService = typeOfService === SERVICE_TYPES.UPCOMING_SERVICE.type;
      const isPreviousService = typeOfService === SERVICE_TYPES.PREVIOUS_SERVICE.type;
      const isUpcomingLgqService = !!this.scheduleEvent.long_grass_quote_service;

      if (
        (isUpcomingService && isLGF) ||
        (isUpcomingService && !isUpcomingLgqService) ||
        (isUpcomingService && isLGQ && isPending) ||
        (isPreviousService && isLGQ && !isPending && !isUpcomingLgqService)
      )
        return false;

      return isScopeAvailable;
    },
  },
  methods: {
    getDateText({ startDate, endDate }) {
      return lsDateHelpers.getDateRange({
        startDate,
        endDate,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-service-details-card--header {
  font-size: $size-4;
  color: $primary;
  font-weight: bold;
  border-top: 1px solid $primary-light;
  display: flex;
  padding-top: 2.5rem;
}

.ls-service-details-card--nonLtrs {
  margin-top: 20px;
}

.ls-service-details-card--ltrs-status {
  color: $primary;
  font-weight: 700;
}

.ls-service-details-card {
  border-bottom: 1px solid $primary-light;

  > .ls-service-details-card--sub-heading {
    font-size: $size-4;
    color: $primary;
    font-weight: bold;
  }

  > div {
    margin: 10px 0 20px 0;
    background: $primary-light;
    padding: 18px 18px 16px 18px;
    display: flex;
    > div {
      margin-top: 1px;
      margin-left: 10px;
      span {
        font-size: $size-5;
        color: $primary;
        &:nth-of-type(2) {
          font-weight: bold;
        }
      }
    }
  }
  .ls-service-details-card--service {
    align-items: center;
    display: flex;
    .ls-service-details-card--long-grass-report {
      align-items: center;
      color: $theme-color-social-twitter;
      display: inline-flex;
      margin-left: auto;
      &:hover {
        color: $theme-color-social-twitter;
      }
    }
  }
}
</style>
